// Supported interface languages.
//
// Each entry is a self-contained catalogue: adding another language means
// adding one object here and nothing else - no component changes, no build
// step. `native` is what the user sees in the picker, because someone looking
// for Tamil is looking for their own script, not the English word "Tamil".
//
// Keys are dotted and grouped by area. A key with no translation falls back to
// English (see LanguageContext), so a partially translated language is always
// usable rather than showing raw keys on screen.

export const LANGUAGES = [
  { code: 'en', native: 'English', label: 'English' },
  { code: 'hi', native: 'हिन्दी', label: 'Hindi' },
  { code: 'mr', native: 'मराठी', label: 'Marathi' },
  { code: 'gu', native: 'ગુજરાતી', label: 'Gujarati' },
  { code: 'ta', native: 'தமிழ்', label: 'Tamil' },
  { code: 'te', native: 'తెలుగు', label: 'Telugu' },
  { code: 'bn', native: 'বাংলা', label: 'Bengali' },
];

export const DEFAULT_LANGUAGE = 'en';

// English is the reference catalogue. Every other language is a subset of these
// keys; anything missing falls back to the English string below.
const en = {
  'nav.reception': 'Reception',
  'nav.restaurant': 'Restaurant',
  'nav.services': 'Services',
  'nav.store': 'Store',
  'nav.accounts': 'Accounts',
  'nav.staff': 'Staff',
  'nav.reports': 'Reports',
  'nav.setup': 'Setup',
  'nav.users': 'Users',

  'nav.dashboard': 'Dashboard',
  'nav.orders': 'Orders',
  'nav.kitchen': 'Kitchen',
  'nav.tables': 'Tables',
  'nav.bookings': 'Bookings',
  'nav.payments': 'Payments',
  'nav.stock': 'Stock',
  'nav.language': 'Language',

  'action.save': 'Save',
  'action.cancel': 'Cancel',
  'action.delete': 'Delete',
  'action.edit': 'Edit',
  'action.add': 'Add',
  'action.search': 'Search',
  'action.print': 'Print',
  'action.close': 'Close',
  'action.back': 'Back',
  'action.next': 'Next',
  'action.confirm': 'Confirm',
  'action.logout': 'Logout',

  'label.total': 'Total',
  'label.date': 'Date',
  'label.time': 'Time',
  'label.status': 'Status',
  'label.amount': 'Amount',
  'label.quantity': 'Quantity',
  'label.name': 'Name',
  'label.mobile': 'Mobile',
  'label.table': 'Table',
  'label.room': 'Room',
  'label.customer': 'Customer',
  'label.item': 'Item',
  'label.price': 'Price',
  'label.paid': 'Paid',
  'label.due': 'Due',

  'msg.loading': 'Loading...',
  'msg.noData': 'No data found',
  'msg.saved': 'Saved successfully',
  'msg.error': 'Something went wrong',

  'lang.title': 'Language',
  'lang.subtitle': 'Choose the language for the application interface.',
  'lang.current': 'Current language',
  'lang.changed': 'Language changed',
  'lang.note':
    'This changes the interface language only. Printed bills, KOTs and reports continue to use the language they are configured with.',
};

const hi = {
  'nav.reception': 'रिसेप्शन',
  'nav.restaurant': 'रेस्टोरेंट',
  'nav.services': 'सेवाएँ',
  'nav.store': 'स्टोर',
  'nav.accounts': 'खाते',
  'nav.staff': 'कर्मचारी',
  'nav.reports': 'रिपोर्ट',
  'nav.setup': 'सेटअप',
  'nav.users': 'उपयोगकर्ता',

  'nav.dashboard': 'डैशबोर्ड',
  'nav.orders': 'ऑर्डर',
  'nav.kitchen': 'रसोई',
  'nav.tables': 'टेबल',
  'nav.bookings': 'बुकिंग',
  'nav.payments': 'भुगतान',
  'nav.stock': 'स्टॉक',
  'nav.language': 'भाषा',

  'action.save': 'सहेजें',
  'action.cancel': 'रद्द करें',
  'action.delete': 'हटाएँ',
  'action.edit': 'बदलें',
  'action.add': 'जोड़ें',
  'action.search': 'खोजें',
  'action.print': 'प्रिंट',
  'action.close': 'बंद करें',
  'action.back': 'पीछे',
  'action.next': 'आगे',
  'action.confirm': 'पुष्टि करें',
  'action.logout': 'लॉगआउट',

  'label.total': 'कुल',
  'label.date': 'तारीख',
  'label.time': 'समय',
  'label.status': 'स्थिति',
  'label.amount': 'राशि',
  'label.quantity': 'मात्रा',
  'label.name': 'नाम',
  'label.mobile': 'मोबाइल',
  'label.table': 'टेबल',
  'label.room': 'कमरा',
  'label.customer': 'ग्राहक',
  'label.item': 'आइटम',
  'label.price': 'कीमत',
  'label.paid': 'भुगतान हुआ',
  'label.due': 'बकाया',

  'msg.loading': 'लोड हो रहा है...',
  'msg.noData': 'कोई डेटा नहीं मिला',
  'msg.saved': 'सफलतापूर्वक सहेजा गया',
  'msg.error': 'कुछ गड़बड़ हो गई',

  'lang.title': 'भाषा',
  'lang.subtitle': 'एप्लिकेशन के लिए भाषा चुनें।',
  'lang.current': 'वर्तमान भाषा',
  'lang.changed': 'भाषा बदल दी गई',
  'lang.note':
    'इससे सिर्फ़ स्क्रीन की भाषा बदलती है। बिल, KOT और रिपोर्ट अपनी तय की गई भाषा में ही छपते रहेंगे।',
};
const mr = {
  'nav.reception': 'रिसेप्शन',
  'nav.restaurant': 'रेस्टॉरंट',
  'nav.services': 'सेवा',
  'nav.store': 'स्टोअर',
  'nav.accounts': 'खाती',
  'nav.staff': 'कर्मचारी',
  'nav.reports': 'अहवाल',
  'nav.setup': 'सेटअप',
  'nav.users': 'वापरकर्ते',

  'nav.dashboard': 'डॅशबोर्ड',
  'nav.orders': 'ऑर्डर',
  'nav.kitchen': 'स्वयंपाकघर',
  'nav.tables': 'टेबल',
  'nav.bookings': 'बुकिंग',
  'nav.payments': 'देयके',
  'nav.stock': 'साठा',
  'nav.language': 'भाषा',

  'action.save': 'जतन करा',
  'action.cancel': 'रद्द करा',
  'action.delete': 'हटवा',
  'action.edit': 'बदला',
  'action.add': 'जोडा',
  'action.search': 'शोधा',
  'action.print': 'प्रिंट',
  'action.close': 'बंद करा',
  'action.back': 'मागे',
  'action.next': 'पुढे',
  'action.confirm': 'खात्री करा',
  'action.logout': 'बाहेर पडा',

  'label.total': 'एकूण',
  'label.date': 'तारीख',
  'label.time': 'वेळ',
  'label.status': 'स्थिती',
  'label.amount': 'रक्कम',
  'label.quantity': 'संख्या',
  'label.name': 'नाव',
  'label.mobile': 'मोबाइल',
  'label.table': 'टेबल',
  'label.room': 'खोली',
  'label.customer': 'ग्राहक',
  'label.item': 'वस्तू',
  'label.price': 'किंमत',
  'label.paid': 'भरले',
  'label.due': 'बाकी',

  'msg.loading': 'लोड होत आहे...',
  'msg.noData': 'माहिती मिळाली नाही',
  'msg.saved': 'यशस्वीरित्या जतन झाले',
  'msg.error': 'काहीतरी चूक झाली',

  'lang.title': 'भाषा',
  'lang.subtitle': 'अ‍ॅप्लिकेशनसाठी भाषा निवडा.',
  'lang.current': 'सध्याची भाषा',
  'lang.changed': 'भाषा बदलली',
};

const gu = {
  'nav.reception': 'રિસેપ્શન',
  'nav.restaurant': 'રેસ્ટોરન્ટ',
  'nav.services': 'સેવાઓ',
  'nav.store': 'સ્ટોર',
  'nav.accounts': 'ખાતાં',
  'nav.staff': 'કર્મચારી',
  'nav.reports': 'રિપોર્ટ',
  'nav.setup': 'સેટઅપ',
  'nav.users': 'વપરાશકર્તા',

  'nav.dashboard': 'ડેશબોર્ડ',
  'nav.orders': 'ઓર્ડર',
  'nav.kitchen': 'રસોડું',
  'nav.tables': 'ટેબલ',
  'nav.bookings': 'બુકિંગ',
  'nav.payments': 'ચુકવણી',
  'nav.stock': 'સ્ટોક',
  'nav.language': 'ભાષા',

  'action.save': 'સાચવો',
  'action.cancel': 'રદ કરો',
  'action.delete': 'કાઢી નાખો',
  'action.edit': 'ફેરફાર',
  'action.add': 'ઉમેરો',
  'action.search': 'શોધો',
  'action.print': 'પ્રિન્ટ',
  'action.close': 'બંધ કરો',
  'action.back': 'પાછળ',
  'action.next': 'આગળ',
  'action.confirm': 'ખાતરી કરો',
  'action.logout': 'લોગઆઉટ',

  'label.total': 'કુલ',
  'label.date': 'તારીખ',
  'label.time': 'સમય',
  'label.status': 'સ્થિતિ',
  'label.amount': 'રકમ',
  'label.quantity': 'જથ્થો',
  'label.name': 'નામ',
  'label.mobile': 'મોબાઇલ',
  'label.table': 'ટેબલ',
  'label.room': 'રૂમ',
  'label.customer': 'ગ્રાહક',
  'label.item': 'આઇટમ',
  'label.price': 'કિંમત',
  'label.paid': 'ચૂકવેલ',
  'label.due': 'બાકી',

  'msg.loading': 'લોડ થઈ રહ્યું છે...',
  'msg.noData': 'કોઈ માહિતી મળી નથી',
  'msg.saved': 'સફળતાપૂર્વક સાચવ્યું',
  'msg.error': 'કંઈક ખોટું થયું',

  'lang.title': 'ભાષા',
  'lang.subtitle': 'એપ્લિકેશન માટે ભાષા પસંદ કરો.',
  'lang.current': 'હાલની ભાષા',
  'lang.changed': 'ભાષા બદલાઈ',
};
const ta = {
  'nav.reception': 'வரவேற்பு',
  'nav.restaurant': 'உணவகம்',
  'nav.services': 'சேவைகள்',
  'nav.store': 'கிடங்கு',
  'nav.accounts': 'கணக்குகள்',
  'nav.staff': 'ஊழியர்கள்',
  'nav.reports': 'அறிக்கைகள்',
  'nav.setup': 'அமைப்பு',
  'nav.users': 'பயனர்கள்',

  'nav.dashboard': 'டாஷ்போர்டு',
  'nav.orders': 'ஆர்டர்கள்',
  'nav.kitchen': 'சமையலறை',
  'nav.tables': 'மேசைகள்',
  'nav.bookings': 'முன்பதிவுகள்',
  'nav.payments': 'கட்டணங்கள்',
  'nav.stock': 'இருப்பு',
  'nav.language': 'மொழி',

  'action.save': 'சேமி',
  'action.cancel': 'ரத்து',
  'action.delete': 'நீக்கு',
  'action.edit': 'திருத்து',
  'action.add': 'சேர்',
  'action.search': 'தேடு',
  'action.print': 'அச்சிடு',
  'action.close': 'மூடு',
  'action.back': 'பின்',
  'action.next': 'அடுத்து',
  'action.confirm': 'உறுதி செய்',
  'action.logout': 'வெளியேறு',

  'label.total': 'மொத்தம்',
  'label.date': 'தேதி',
  'label.time': 'நேரம்',
  'label.status': 'நிலை',
  'label.amount': 'தொகை',
  'label.quantity': 'அளவு',
  'label.name': 'பெயர்',
  'label.mobile': 'கைபேசி',
  'label.table': 'மேசை',
  'label.room': 'அறை',
  'label.customer': 'வாடிக்கையாளர்',
  'label.item': 'பொருள்',
  'label.price': 'விலை',
  'label.paid': 'செலுத்தியது',
  'label.due': 'நிலுவை',

  'msg.loading': 'ஏற்றுகிறது...',
  'msg.noData': 'தரவு இல்லை',
  'msg.saved': 'வெற்றிகரமாக சேமிக்கப்பட்டது',
  'msg.error': 'ஏதோ தவறு நடந்தது',

  'lang.title': 'மொழி',
  'lang.subtitle': 'பயன்பாட்டிற்கான மொழியைத் தேர்ந்தெடுக்கவும்.',
  'lang.current': 'தற்போதைய மொழி',
  'lang.changed': 'மொழி மாற்றப்பட்டது',
};

const te = {
  'nav.reception': 'రిసెప్షన్',
  'nav.restaurant': 'రెస్టారెంట్',
  'nav.services': 'సేవలు',
  'nav.store': 'స్టోర్',
  'nav.accounts': 'ఖాతాలు',
  'nav.staff': 'సిబ్బంది',
  'nav.reports': 'నివేదికలు',
  'nav.setup': 'సెటప్',
  'nav.users': 'వినియోగదారులు',

  'nav.dashboard': 'డాష్‌బోర్డ్',
  'nav.orders': 'ఆర్డర్లు',
  'nav.kitchen': 'వంటగది',
  'nav.tables': 'టేబుళ్లు',
  'nav.bookings': 'బుకింగ్‌లు',
  'nav.payments': 'చెల్లింపులు',
  'nav.stock': 'నిల్వ',
  'nav.language': 'భాష',

  'action.save': 'సేవ్ చేయి',
  'action.cancel': 'రద్దు',
  'action.delete': 'తొలగించు',
  'action.edit': 'మార్చు',
  'action.add': 'జోడించు',
  'action.search': 'వెతుకు',
  'action.print': 'ప్రింట్',
  'action.close': 'మూసివేయి',
  'action.back': 'వెనుకకు',
  'action.next': 'తదుపరి',
  'action.confirm': 'నిర్ధారించు',
  'action.logout': 'లాగ్అవుట్',

  'label.total': 'మొత్తం',
  'label.date': 'తేదీ',
  'label.time': 'సమయం',
  'label.status': 'స్థితి',
  'label.amount': 'మొత్తం',
  'label.quantity': 'పరిమాణం',
  'label.name': 'పేరు',
  'label.mobile': 'మొబైల్',
  'label.table': 'టేబుల్',
  'label.room': 'గది',
  'label.customer': 'కస్టమర్',
  'label.item': 'వస్తువు',
  'label.price': 'ధర',
  'label.paid': 'చెల్లించారు',
  'label.due': 'బాకీ',

  'msg.loading': 'లోడ్ అవుతోంది...',
  'msg.noData': 'డేటా లేదు',
  'msg.saved': 'విజయవంతంగా సేవ్ అయింది',
  'msg.error': 'ఏదో తప్పు జరిగింది',

  'lang.title': 'భాష',
  'lang.subtitle': 'అప్లికేషన్ కోసం భాషను ఎంచుకోండి.',
  'lang.current': 'ప్రస్తుత భాష',
  'lang.changed': 'భాష మార్చబడింది',
};

const bn = {
  'nav.reception': 'অভ্যর্থনা',
  'nav.restaurant': 'রেস্তোরাঁ',
  'nav.services': 'পরিষেবা',
  'nav.store': 'স্টোর',
  'nav.accounts': 'হিসাব',
  'nav.staff': 'কর্মী',
  'nav.reports': 'রিপোর্ট',
  'nav.setup': 'সেটআপ',
  'nav.users': 'ব্যবহারকারী',

  'nav.dashboard': 'ড্যাশবোর্ড',
  'nav.orders': 'অর্ডার',
  'nav.kitchen': 'রান্নাঘর',
  'nav.tables': 'টেবিল',
  'nav.bookings': 'বুকিং',
  'nav.payments': 'পেমেন্ট',
  'nav.stock': 'স্টক',
  'nav.language': 'ভাষা',

  'action.save': 'সংরক্ষণ',
  'action.cancel': 'বাতিল',
  'action.delete': 'মুছুন',
  'action.edit': 'সম্পাদনা',
  'action.add': 'যোগ করুন',
  'action.search': 'খুঁজুন',
  'action.print': 'প্রিন্ট',
  'action.close': 'বন্ধ',
  'action.back': 'পিছনে',
  'action.next': 'পরবর্তী',
  'action.confirm': 'নিশ্চিত করুন',
  'action.logout': 'লগআউট',

  'label.total': 'মোট',
  'label.date': 'তারিখ',
  'label.time': 'সময়',
  'label.status': 'অবস্থা',
  'label.amount': 'পরিমাণ',
  'label.quantity': 'সংখ্যা',
  'label.name': 'নাম',
  'label.mobile': 'মোবাইল',
  'label.table': 'টেবিল',
  'label.room': 'ঘর',
  'label.customer': 'গ্রাহক',
  'label.item': 'আইটেম',
  'label.price': 'দাম',
  'label.paid': 'পরিশোধিত',
  'label.due': 'বাকি',

  'msg.loading': 'লোড হচ্ছে...',
  'msg.noData': 'কোনো তথ্য নেই',
  'msg.saved': 'সফলভাবে সংরক্ষিত',
  'msg.error': 'কিছু ভুল হয়েছে',

  'lang.title': 'ভাষা',
  'lang.subtitle': 'অ্যাপ্লিকেশনের জন্য ভাষা নির্বাচন করুন।',
  'lang.current': 'বর্তমান ভাষা',
  'lang.changed': 'ভাষা পরিবর্তিত হয়েছে',
};

export const CATALOGUES = { en, hi, mr, gu, ta, te, bn };